import React, { useState, useEffect } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { TENANT_ID, loadTenantAuthContext, loadTenantConfig } from '../tenantloader/TenantLoader';
import Login from '../tenantloader/Login';
import ReviewPage from './ReviewPage';

const NotFoundScreen = () => (
  <Box sx={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    flexDirection: 'column',
    gap: 2,
    bgcolor: 'background.default',
    fontFamily: 'Roboto, sans-serif'
  }}>
    <Typography variant="h3" sx={{ fontWeight: 500 }}>404</Typography>
    <Typography variant="h6" color="text.secondary">Not found</Typography>
    <Typography variant="body2" color="text.secondary">
      This page is not available for this site.
    </Typography>
  </Box>
);

const LoadingScreen = () => (
  <Box sx={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    flexDirection: 'column',
    gap: 2,
    bgcolor: 'background.default',
    fontFamily: 'Roboto, sans-serif'
  }}>
    <CircularProgress size={40} />
    <Box sx={{ textAlign: 'center' }}>
      <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 500 }}>
        Loading...
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Please wait
      </Typography>
    </Box>
  </Box>
);

/**
 * Inner component that has access to tenant's useAuth and passes auth + tenant config to ReviewPage.
 * When adminView=true and the user is not logged in, renders the Login page instead.
 */
function ReviewPageWithAuth({ requestId, accessKeyFromUrl, tenantId, tenantConfig, adminView, initialSearch, appId, sha256 }) {
  const tenantAuth = loadTenantAuthContext();
  const { useAuth } = tenantAuth;
  const { currentUser, loading, logout } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (adminView && !currentUser) {
    return <Login />;
  }

  const getIdToken = currentUser && typeof currentUser.getIdToken === 'function'
    ? () => currentUser.getIdToken()
    : null;

  return (
    <ReviewPage
      requestId={requestId}
      accessKeyFromUrl={accessKeyFromUrl}
      tenantId={tenantId}
      currentUser={currentUser}
      getIdToken={getIdToken}
      tenantConfig={tenantConfig}
      onLogout={logout}
      initialSearch={initialSearch}
      appId={appId}
      sha256={sha256}
    />
  );
}

/**
 * Review app wrapper: loads the current tenant's Firebase and AuthProvider,
 * then renders ReviewPage inside that auth context so it can use currentUser when present.
 * When TENANT_ID is null (unknown host), shows 404 Not found instead of loading the report.
 */
export default function ReviewApp({ requestId, accessKeyFromUrl, adminView, initialSearch, appId, sha256 }) {
  if (!TENANT_ID) {
    return <NotFoundScreen />;
  }

  const tenantAuth = loadTenantAuthContext();
  const { AuthProvider } = tenantAuth;

  return (
    <AuthProvider>
      <ReviewAppContent
        requestId={requestId}
        accessKeyFromUrl={accessKeyFromUrl}
        tenantId={TENANT_ID}
        adminView={adminView}
        initialSearch={initialSearch}
        appId={appId}
        sha256={sha256}
      />
    </AuthProvider>
  );
}

/**
 * Loads tenant config then renders ReviewPageWithAuth (so header can show tenant name/logo).
 */
function ReviewAppContent({ requestId, accessKeyFromUrl, tenantId, adminView, initialSearch, appId, sha256 }) {
  const [tenantConfig, setTenantConfig] = useState(null);

  useEffect(() => {
    let cancelled = false;
    loadTenantConfig().then((config) => {
      if (!cancelled) setTenantConfig(config);
    }).catch((err) => {
      if (!cancelled) console.error('ReviewApp: failed to load tenant config', err);
    });
    return () => { cancelled = true; };
  }, []);

  return (
    <ReviewPageWithAuth
      requestId={requestId}
      accessKeyFromUrl={accessKeyFromUrl}
      tenantId={tenantId}
      tenantConfig={tenantConfig}
      adminView={adminView}
      initialSearch={initialSearch}
      appId={appId}
      sha256={sha256}
    />
  );
}
